import { Badge, makeStyles, tokens } from '@fluentui/react-components';
import { useTranslation } from 'react-i18next';
import { useShowcaseStyles, SHOWCASE_ACCENT, type ShowcaseAccent } from '../../shared/narrative/showcase-styles';
import { useCopilotRail } from '../../../copilot-rail/rail-context';
import { enrichWithLiveAnswer, startInsight, startReco } from './start-rail';
import { WORK_MODES, WORK_CHART_FIT_ROWS, type WorkModeId } from './start-content';
import { START_NARRATIVE_NARROW_MEDIA_QUERY } from './start-layout';

// Per-mode left-accent (Frontier-Firm operating model: humans navy, agents green, on-demand violet).
const MODE_ACCENT: Record<WorkModeId, ShowcaseAccent> = {
  humans: 'navy',
  agents: 'green',
  'on-demand': 'violet',
};

const useStyles = makeStyles({
  root: {
    display: 'grid',
    gap: tokens.spacingVerticalL,
    minWidth: 0,
  },
  flow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(3, minmax(0, 1fr))',
    gap: tokens.spacingHorizontalM,
    listStyleType: 'none',
    margin: 0,
    padding: 0,
    [START_NARRATIVE_NARROW_MEDIA_QUERY]: {
      gridTemplateColumns: '1fr',
    },
  },
  flowItem: {
    minWidth: 0,
    display: 'flex',
  },
  badge: { alignSelf: 'flex-start' },
  fitCard: {
    display: 'grid',
    gap: tokens.spacingVerticalM,
    padding: tokens.spacingHorizontalL,
    borderRadius: tokens.borderRadiusXLarge,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    backgroundColor: tokens.colorNeutralBackground1,
    boxShadow: tokens.shadow2,
    minWidth: 0,
  },
  fitBody: {
    margin: 0,
    fontSize: tokens.fontSizeBase300,
    color: tokens.colorNeutralForeground2,
    lineHeight: 1.5,
  },
  fitTable: {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: tokens.fontSizeBase200,
  },
  fitTh: {
    textAlign: 'left',
    verticalAlign: 'top',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  fitTd: {
    verticalAlign: 'top',
    padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
    color: tokens.colorNeutralForeground2,
  },
});

export function WorkChartSection() {
  const styles = useStyles();
  const sc = useShowcaseStyles();
  const { t } = useTranslation();
  let rail: ReturnType<typeof useCopilotRail> | null = null;
  try {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    rail = useCopilotRail();
  } catch {
    rail = null;
  }

  return (
    <div className={styles.root}>
      <ol className={styles.flow} aria-label={t('start.frontier.workChart.flowLabel')}>
        {WORK_MODES.map((mode) => {
          const accent = SHOWCASE_ACCENT[MODE_ACCENT[mode.id]];
          return (
            <li key={mode.id} className={styles.flowItem} data-testid="work-chart-mode">
              <button
                type="button"
                className={sc.accentCard}
                style={{ borderLeftColor: accent }}
                onClick={() => {
                  rail?.openWithReco(
                    startInsight(`work-chart-${mode.id}`, t(mode.titleKey)),
                    startReco(
                      t(mode.titleKey),
                      t(mode.bodyKey),
                      [t('start.frontier.workChart.fit.title')],
                      ['hcp:OperatingModel'],
                    ),
                  );
                  if (rail) {
                    void enrichWithLiveAnswer(t(mode.bodyKey), rail).catch((error) => {
                      console.error('PO agent live enrichment failed', error);
                    });
                  }
                }}
              >
                <Badge className={styles.badge} appearance="tint" color="brand">
                  {t('start.frontier.workChart.modeBadge')}
                </Badge>
                <span className={sc.cardTitle} data-testid="work-chart-mode-title">
                  {t(mode.titleKey)}
                </span>
                <span className={sc.cardBody}>{t(mode.bodyKey)}</span>
              </button>
            </li>
          );
        })}
      </ol>

      <section className={styles.fitCard} aria-label={t('start.frontier.workChart.fit.title')}>
        <p className={styles.fitBody}>{t('start.frontier.workChart.fit.body')}</p>
        <table className={styles.fitTable} aria-label={t('start.frontier.workChart.fit.title')}>
          <thead>
            <tr>
              <th scope="col" className={styles.fitTh}>
                {t('start.frontier.workChart.fit.columns.principle')}
              </th>
              <th scope="col" className={styles.fitTh}>
                {t('start.frontier.workChart.fit.columns.curavias')}
              </th>
            </tr>
          </thead>
          <tbody>
            {WORK_CHART_FIT_ROWS.map((row) => (
              <tr key={row.id} data-testid="work-chart-fit-row">
                <th scope="row" className={styles.fitTh}>
                  {t(row.principleKey)}
                </th>
                <td className={styles.fitTd}>{t(row.curaviasKey)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>
    </div>
  );
}
